/**
 * Tris de la marketplace, mêmes choix et libellés que MarketplaceChromeHelper::sortOptions() (rendu
 * serveur). La recherche thématique garde les neuf tris de SortBy.vue ; les autres pages proposent six
 * tris sur tous les écrans (les cinq du téléphone, puis « Prix en baisse »).
 *
 * Les anciens tris (puissance, année) ne sont plus proposés mais restent compris : un ancien lien
 * `?sortBy=power_desc` trie au premier rendu serveur ; ils doivent aussi exister pour InstantSearch, sinon
 * sa première recherche revient au tri par défaut. Ils sont donc dans les items, marqués `hidden`, et
 * n'apparaissent dans un menu que s'ils sont le tri en cours.
 */
export interface SortItem {
    value: string;
    label: string;
    hidden?: boolean;
}

const CURATED: [suffix: string, label: string][] = [
    ["", "Défaut"],
    ["/sort/monthly_price:asc", "Prix croissant"],
    ["/sort/monthly_price:desc", "Prix décroissant"],
    ["/sort/mileage:asc", "Kilométrage croissant"],
    ["/sort/mileage:desc", "Kilométrage décroissant"],
    ["/sort/power:asc", "Puissance croissante"],
    ["/sort/power:desc", "Puissance décroissante"],
    ["/sort/registration_at:asc", "Année croissante"],
    ["/sort/registration_at:desc", "Année décroissante"],
];

const MARKETPLACE: [suffix: string, label: string][] = [
    ["", "Pertinence"],
    ["/sort/monthly_price:asc", "Mensualité croissante"],
    ["/sort/monthly_price:desc", "Mensualité décroissante"],
    ["/sort/mileage:asc", "Kilométrage croissant"],
    ["/sort/mileage:desc", "Kilométrage décroissant"],
    ["/sort/promotion_value:desc", "Prix en baisse"],
];

/** Anciens tris, compris mais plus proposés (hors recherche thématique). */
const LEGACY: [suffix: string, label: string][] = [
    ["/sort/power:asc", "Puissance croissante"],
    ["/sort/power:desc", "Puissance décroissante"],
    ["/sort/registration_at:asc", "Année croissante"],
    ["/sort/registration_at:desc", "Année décroissante"],
];

export function marketplaceSortItems(indexName: string, curated: boolean): SortItem[] {
    const toItem = ([suffix, label]: [string, string], hidden = false): SortItem => ({
        value: `${indexName}${suffix}`,
        label,
        ...(hidden ? { hidden: true } : {}),
    });

    return curated
        ? CURATED.map((sort) => toItem(sort))
        : [...MARKETPLACE.map((sort) => toItem(sort)), ...LEGACY.map((sort) => toItem(sort, true))];
}

/** Choix à afficher : sans les anciens tris, sauf celui en cours (un menu doit pouvoir le nommer). */
export function visibleSortOptions<T extends { value: string }>(
    options: T[],
    items: SortItem[],
    currentRefinement: string,
): T[] {
    const hidden = new Set(items.filter((item) => item.hidden).map((item) => item.value));

    return options.filter((option) => !hidden.has(option.value) || option.value === currentRefinement);
}
